import { useQuery } from "@tanstack/react-query";
import { LoaderIcon, MicIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/glossary";
import { getRecordMime } from "@/lib/audio/mime";
import { cn } from "@/lib/cn";
import {
  RECORDING_ORANGE_START_RATIO,
  RECORDING_START_MS,
} from "@/lib/constants";

type RecordingState =
  | { type: "idle" }
  | { type: "starting" }
  | { type: "recording" }
  | { type: "error"; message: string };

type RecordButtonProps = {
  onRecorded: (recording_b64: string) => void;
  onRecordingStart?: () => void;
  recordingDurationMs: number;
  idleLabel?: string;
  isPending?: boolean;
  className?: string;
};

const getAudioConstraints = (): MediaTrackConstraints => {
  const constraints: MediaTrackConstraints = {
    autoGainControl: false,
    noiseSuppression: false,
    echoCancellation: false,
  };
  if (navigator.mediaDevices.getSupportedConstraints().sampleRate) {
    constraints.sampleRate = { ideal: 48000 };
  }
  return constraints;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",").pop() ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const RecordButton = ({
  onRecorded,
  onRecordingStart,
  recordingDurationMs,
  idleLabel,
  isPending,
  className,
}: RecordButtonProps) => {
  const t = useT();
  const micPermission = useQuery({
    queryKey: ["mic-permission"],
    queryFn: async () => {
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return status.state === "granted";
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const hasPermission = !micPermission.isLoading && micPermission.data === true;
  const [recordingState, setRecordingState] = useState<RecordingState>({
    type: "idle",
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;
  const onRecordingStartRef = useRef(onRecordingStart);
  onRecordingStartRef.current = onRecordingStart;
  const recordingDurationMsRef = useRef(recordingDurationMs);
  recordingDurationMsRef.current = recordingDurationMs;
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStartPendingRef = useRef(false);

  const clearStopTimeout = useCallback(() => {
    if (stopTimeoutRef.current != null) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, []);

  const clearPhaseTimeout = useCallback(() => {
    if (phaseTimeoutRef.current != null) {
      clearTimeout(phaseTimeoutRef.current);
      phaseTimeoutRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    clearStopTimeout();
    mediaRecorderRef.current?.stop();
  }, [clearStopTimeout]);

  useEffect(
    () => () => {
      clearStopTimeout();
      clearPhaseTimeout();
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      } else {
        releaseStream();
      }
    },
    [clearPhaseTimeout, clearStopTimeout, releaseStream],
  );

  const start = useCallback(async () => {
    if (
      isStartPendingRef.current ||
      mediaRecorderRef.current?.state === "recording"
    )
      return;
    isStartPendingRef.current = true;
    setRecordingState({ type: "starting" });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getAudioConstraints(),
      });
      streamRef.current = stream;

      const actualRate = stream.getAudioTracks()[0]?.getSettings().sampleRate;
      console.log("[mic] actual sampleRate:", actualRate);

      const mime = getRecordMime();
      const mediaRecorder = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined,
      );
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        mediaRecorderRef.current = null;
        clearStopTimeout();
        clearPhaseTimeout();
        const blob = new Blob(chunksRef.current, {
          type: mime || mediaRecorder.mimeType,
        });
        releaseStream();
        setRecordingState({ type: "idle" });
        onRecordedRef.current(await blobToBase64(blob));
      };

      mediaRecorder.start();
      onRecordingStartRef.current?.();
      setRecordingState({ type: "starting" });
      clearPhaseTimeout();
      phaseTimeoutRef.current = setTimeout(() => {
        setRecordingState({ type: "recording" });
        phaseTimeoutRef.current = null;
      }, RECORDING_START_MS);
      clearStopTimeout();
      stopTimeoutRef.current = setTimeout(stop, recordingDurationMsRef.current);
    } catch (e) {
      releaseStream();
      setRecordingState({
        type: "error",
        message: e instanceof Error ? e.message : "Failed to start recording",
      });
    } finally {
      isStartPendingRef.current = false;
    }
  }, [clearPhaseTimeout, clearStopTimeout, releaseStream, stop]);

  const reset = useCallback(() => {
    clearStopTimeout();
    clearPhaseTimeout();
    isStartPendingRef.current = false;
    mediaRecorderRef.current = null;
    releaseStream();
    setRecordingState({ type: "idle" });
  }, [clearPhaseTimeout, clearStopTimeout, releaseStream]);

  const isRecording = recordingState.type === "recording";
  const isStarting = recordingState.type === "starting";
  const isCapturing = isStarting || isRecording;
  const isError = recordingState.type === "error";
  const buttonLabel = isStarting
    ? t.prod.starting
    : isRecording
      ? t.prod.recording
      : isPending
        ? t.prod.analyzing
        : (idleLabel ?? t.prod.speak);

  if (isError) {
    const isDenied = recordingState.message.toLowerCase().includes("denied");
    return (
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        <p className="text-destructive">{recordingState.message}</p>
        {isDenied && (
          <p className="text-muted-foreground text-sm">
            {t.errors.microphoneInstructions}
          </p>
        )}
        <Button onClick={reset}>{t.common.tryAgain}</Button>
      </div>
    );
  }

  if (!hasPermission && !micPermission.isLoading && !isError) {
    return (
      <button
        type="button"
        className={cn(
          "flex w-full select-none items-center justify-center gap-3 rounded-full border-2 border-foreground/80 bg-foreground px-8 py-5 font-bold text-background text-lg shadow-lg transition-all duration-200 hover:scale-105 active:scale-95",
          className,
        )}
        onClick={async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: getAudioConstraints(),
            });
            for (const track of stream.getTracks()) track.stop();
            await micPermission.refetch();
          } catch (e) {
            setRecordingState({
              type: "error",
              message:
                e instanceof Error ? e.message : "Microphone access denied",
            });
          }
        }}
      >
        <MicIcon className="h-5 w-5" />
        {t.errors.allowMicrophone}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        "relative flex w-full select-none items-center justify-center gap-3 overflow-hidden rounded-full border px-8 py-5 font-bold text-lg shadow-[0_12px_28px_rgba(0,0,0,0.24)] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50",
        className,
        isCapturing
          ? "recording-capture-live border-orange-500/50 bg-neutral-950 text-white"
          : "border-neutral-950 bg-neutral-950 text-white hover:scale-105 active:scale-95",
        (isPending || isStarting) &&
          "border-neutral-950 bg-neutral-950 text-white",
      )}
      style={{
        touchAction: "manipulation",
        WebkitUserSelect: "none",
        ["--recording-duration" as string]: `${recordingDurationMs}ms`,
        ["--recording-start-ms" as string]: `${RECORDING_START_MS}ms`,
        ["--recording-warmup-duration" as string]: `${Math.round(RECORDING_START_MS * RECORDING_ORANGE_START_RATIO)}ms`,
        ["--recording-orange-transition" as string]: `${RECORDING_START_MS - Math.round(RECORDING_START_MS * RECORDING_ORANGE_START_RATIO)}ms`,
      }}
      disabled={isPending || isStarting}
      onClick={() => {
        if (isPending || isStarting || isRecording) return;
        start();
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.12),rgba(255,255,255,0))] opacity-60"
      />
      <span
        aria-hidden="true"
        className={cn(
          "recording-warmup-layer pointer-events-none absolute inset-0 rounded-full bg-[#6a4b32]",
          isCapturing ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "recording-orange-base pointer-events-none absolute inset-0 rounded-full",
          isCapturing ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "recording-orange-front pointer-events-none absolute inset-0 rounded-full",
          isCapturing ? "opacity-100" : "opacity-0",
        )}
      />
      <span className="relative z-10 flex items-center gap-3">
        {isPending ? (
          <LoaderIcon className="h-5 w-5 animate-spin" />
        ) : (
          <MicIcon className={cn("h-5 w-5", isRecording && "drop-shadow-sm")} />
        )}
        <span>{buttonLabel}</span>
      </span>
    </button>
  );
};
